import React, { useState } from "react";
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";

import { Organisation, User, UserType } from "../../../types";
import TableViewShell from "../../../components/views/table-view-shell";
import UsersTableRow from "./users-table-row";

interface UsersTableProps {
  filteredUsers: User[];
  unfilteredUserCount: number;
  teamDictionary: Record<string, Organisation>;
}

type UserSortField = "name" | "email" | "clientRole";

const USER_ROLE_SORT_ORDER: UserType[] = [
  UserType.DeveloperAdmin,
  UserType.SysAdmin,
  UserType.OrgAdmin,
  UserType.User,
  UserType.Guest,
  UserType.Inactive,
  UserType.Blocked,
];

const getUserRoleSortRank = (user: User) => {
  const rank = USER_ROLE_SORT_ORDER.indexOf(user.userType);

  return rank === -1 ? USER_ROLE_SORT_ORDER.length : rank;
};

const UsersTable: React.FC<UsersTableProps> = ({
  filteredUsers,
  unfilteredUserCount,
  teamDictionary,
}) => {
  const [sortBy, setSortBy] = useState<UserSortField>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const headerLabelSx = { fontSize: "0.875rem" };
  const headerCellSx = { py: 0.5, fontSize: "0.875rem", whiteSpace: "nowrap" };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (sortBy === "email") {
      const comparison = (a.email || "").localeCompare(b.email || "", undefined, {
        sensitivity: "base",
      });

      return sortOrder === "asc" ? comparison : -comparison;
    }

    if (sortBy === "clientRole") {
      const rankComparison = getUserRoleSortRank(a) - getUserRoleSortRank(b);

      if (rankComparison !== 0) {
        return sortOrder === "asc" ? rankComparison : -rankComparison;
      }
    }

    const comparison = (a.fullName || a.email || "").localeCompare(
      b.fullName || b.email || "",
      undefined,
      { sensitivity: "base" },
    );

    return sortOrder === "asc" ? comparison : -comparison;
  });

  const handleSort = (field: UserSortField) => {
    if (sortBy === field) {
      setSortOrder((currentOrder) =>
        currentOrder === "asc" ? "desc" : "asc",
      );
      return;
    }

    setSortBy(field);
    setSortOrder("asc");
  };

  const columns = (
    <colgroup>
      <col style={{ width: 220 }} />
      <col style={{ width: 260 }} />
      <col />
      <col style={{ width: 160 }} />
    </colgroup>
  );

  return (
    <TableViewShell
      shownCount={sortedUsers.length}
      totalCount={unfilteredUserCount}
      columns={columns}
      headerAriaLabel="users table header"
      bodyAriaLabel="users table"
      header={
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>
              <TableSortLabel
                active={sortBy === "name"}
                direction={sortBy === "name" ? sortOrder : "asc"}
                onClick={() => handleSort("name")}
                sx={headerLabelSx}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellSx}>
              <TableSortLabel
                active={sortBy === "email"}
                direction={sortBy === "email" ? sortOrder : "asc"}
                onClick={() => handleSort("email")}
                sx={headerLabelSx}
              >
                Email
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellSx}>Teams</TableCell>
            <TableCell align="right" sx={headerCellSx}>
              <TableSortLabel
                active={sortBy === "clientRole"}
                direction={sortBy === "clientRole" ? sortOrder : "asc"}
                onClick={() => handleSort("clientRole")}
                sx={headerLabelSx}
              >
                Role
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
      }
      body={
        <TableBody>
          {sortedUsers.map((user) => {
            const teams = user.organisationIds
              .map((teamId) => teamDictionary[teamId] || null)
              .filter((team): team is Organisation => team !== null);

            return (
              <UsersTableRow
                key={user.userId}
                user={user}
                teams={teams}
              />
            );
          })}
          {sortedUsers.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} sx={{ py: 2 }}>
                <Typography variant="body1">No users found</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      }
    />
  );
};

export default UsersTable;
